import {
  ProjectChat,
  ProjectRunOptionsSelector,
  useCreateWorkChatProject,
  useWorkspaceSnapshot,
  type ObjectWorkspaceWorkbenchContext,
  type ObjectWorkspaceWorkTabDescriptor,
  type ProjectChatProps,
  type ProvisionalSubmitOutcome,
  type SubmitProjectTurnArgs,
} from '@heidi/agents';
import type { ProjectRunFlags } from '@heidi/agents/contracts';
import {
  setOpenArtifactAtom,
  useCreateProjectGuard,
  type ProjectArtifactSink,
  type WorkHomeTaskLaunch,
} from '@heidi/agents/data';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';

import { useLatestRef } from '@heidi/utils';

import {
  linkedContextForObject,
  normalizedObjectWorkspaceTabTitle,
  objectWorkspaceComposerDraftStorageKey,
  objectWorkspaceCreateProjectArgs,
  objectWorkspaceSnapshot,
} from './object-workspace-chat-utils';
import { useObjectWorkspaceSeededCreate } from './use-object-workspace-seeded-create';
import { useObjectWorkspaceTaskLaunches } from './use-object-workspace-task-launches';

const messages = defineMessages({
  composerPlaceholder: {
    defaultMessage: 'How can I help with {name}?',
    id: 'UL9CBT/DRd',
    description: 'Composer placeholder in a patient or encounter object workspace',
  },
});

export interface ObjectWorkspaceChatTab extends ObjectWorkspaceWorkTabDescriptor {
  projectId?: string;
  prefilledPrompt?: string;
  /** Tasks staged from Next Steps; the next send hands them to its Work chat. */
  taskLaunches?: readonly WorkHomeTaskLaunch[];
  isDraftEmpty: boolean;
  hasQueuedTurns: boolean;
  isOpeningTurnInFlight: boolean;
  isTurnInFlight: boolean;
  anchorKey?: string;
  sessionId?: string;
}

export interface ObjectWorkspaceChatTabLifecycle {
  isDraftEmpty?: boolean;
  hasQueuedTurns?: boolean;
  isOpeningTurnInFlight?: boolean;
  isTurnInFlight?: boolean;
}

interface ObjectWorkspaceProjectChatTabProps {
  tab: ObjectWorkspaceChatTab;
  object: ObjectWorkspaceWorkbenchContext['object'];
  isActive: boolean;
  showRunOptions: boolean;
  onBindProject: (tabId: string, projectId: string) => boolean;
  onLifecycleChange: (tabId: string, lifecycle: ObjectWorkspaceChatTabLifecycle) => void;
  onSubmissionError: (tabId: string) => void;
  onSubmissionStart: (tabId: string, prompt: string) => void;
  /** The staged tasks went with a send; drop them from the tab and the pane. */
  onTaskLaunchesTaken: (tabId: string) => void;
}

export function ObjectWorkspaceProjectChatTab({
  tab,
  object,
  isActive,
  showRunOptions,
  onBindProject,
  onLifecycleChange,
  onSubmissionError,
  onSubmissionStart,
  onTaskLaunchesTaken,
}: ObjectWorkspaceProjectChatTabProps): JSX.Element {
  const intl = useIntl();
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);
  const createGuard = useCreateProjectGuard();
  const [runFlags, setRunFlags] = useState<ProjectRunFlags>({});
  const [optimisticFirstTurnText, setOptimisticFirstTurnText] = useState<string | null>(
    null
  );
  const {
    existingProjectForLaunches,
    withLaunchPreamble,
    holdLaunches,
    dropLaunches,
    bindHeldLaunches,
  } = useObjectWorkspaceTaskLaunches();
  const linked = useMemo(() => linkedContextForObject(object), [object]);
  const canvasWorkspace = useWorkspaceSnapshot();
  const workspace = useMemo(
    () => objectWorkspaceSnapshot(canvasWorkspace, object, tab.sessionId),
    [canvasWorkspace, object, tab.sessionId]
  );
  const tabIdRef = useLatestRef(tab.id);
  const onBindProjectRef = useLatestRef(onBindProject);
  const onLifecycleChangeRef = useLatestRef(onLifecycleChange);
  const onSubmissionErrorRef = useLatestRef(onSubmissionError);
  const onSubmissionStartRef = useLatestRef(onSubmissionStart);
  const onTaskLaunchesTakenRef = useLatestRef(onTaskLaunchesTaken);
  const artifactSink = useMemo<ProjectArtifactSink>(
    () => ({
      onOpenArtifact: setOpenArtifact,
      onOpenArtifactSilent: () => undefined,
    }),
    [setOpenArtifact]
  );

  const handleSeededProjectCreated = useCallback(
    (projectId: string) => {
      let bound = false;
      flushSync(() => {
        bound = onBindProjectRef.current(tabIdRef.current, projectId);
      });
      if (bound) bindHeldLaunches(projectId);
      else dropLaunches();
      return bound;
    },
    [bindHeldLaunches, dropLaunches, onBindProjectRef, tabIdRef]
  );
  const handleSeededCreateError = useCallback(() => {
    dropLaunches();
    setOptimisticFirstTurnText(null);
    onSubmissionErrorRef.current(tabIdRef.current);
  }, [dropLaunches, onSubmissionErrorRef, tabIdRef]);
  const { createSeeded, isSubmitting: isSeededCreateSubmitting } =
    useObjectWorkspaceSeededCreate({
      onCreated: handleSeededProjectCreated,
      onCreateError: handleSeededCreateError,
      guard: createGuard,
    });
  const { createWorkChatProject, isSubmitting: isInPlaceCreateSubmitting } =
    useCreateWorkChatProject({ guard: createGuard });
  const isSubmitting = isSeededCreateSubmitting || isInPlaceCreateSubmitting;

  useEffect(() => {
    if (tab.projectId) setOptimisticFirstTurnText(null);
  }, [tab.projectId]);

  useEffect(() => {
    onLifecycleChangeRef.current(tab.id, { isOpeningTurnInFlight: isSubmitting });
  }, [isSubmitting, onLifecycleChangeRef, tab.id]);
  useEffect(
    () => () => {
      onLifecycleChangeRef.current(tabIdRef.current, { isOpeningTurnInFlight: false });
    },
    [onLifecycleChangeRef, tabIdRef]
  );

  const lifecycleHandlers = useMemo(() => {
    const report = (lifecycle: ObjectWorkspaceChatTabLifecycle) =>
      onLifecycleChangeRef.current(tabIdRef.current, lifecycle);
    return {
      onComposerDraftEmptyChange: (isDraftEmpty: boolean) => report({ isDraftEmpty }),
      onHasQueuedTurnsChange: (hasQueuedTurns: boolean) => report({ hasQueuedTurns }),
      onTurnInFlightChange: (isTurnInFlight: boolean) => report({ isTurnInFlight }),
    };
  }, [onLifecycleChangeRef, tabIdRef]);

  const createArgs = useCallback(
    (args: SubmitProjectTurnArgs, title: string) =>
      objectWorkspaceCreateProjectArgs(args, title, {
        linked,
        sessionId: tab.sessionId,
        runFlags,
      }),
    [linked, runFlags, tab.sessionId]
  );

  const handleProvisionalSubmit = useCallback(
    async (args: SubmitProjectTurnArgs): Promise<ProvisionalSubmitOutcome> => {
      const preview = normalizedObjectWorkspaceTabTitle(
        args.displayText ?? args.text,
        tab.title
      );
      const launches = tab.taskLaunches ?? [];
      if (launches.length > 0) {
        const existingProjectId = existingProjectForLaunches(launches, args);
        if (existingProjectId) {
          let bound = false;
          flushSync(() => {
            bound = onBindProjectRef.current(tab.id, existingProjectId);
          });
          if (bound) onTaskLaunchesTakenRef.current(tab.id);
          return bound ? 'consumed' : 'refused';
        }

        const seeded = createArgs(withLaunchPreamble(launches, args), preview);
        holdLaunches(launches);
        setOptimisticFirstTurnText((current) => current ?? preview);
        onSubmissionStartRef.current(tab.id, preview);
        const seededBound = await createSeeded(seeded);
        if (seededBound === null) {
          dropLaunches();
          setOptimisticFirstTurnText(null);
          return 'refused';
        }
        if (seededBound) onTaskLaunchesTakenRef.current(tab.id);
        return 'consumed';
      }

      setOptimisticFirstTurnText((current) => current ?? preview);
      onSubmissionStartRef.current(tab.id, preview);

      let handle;
      try {
        handle = await createWorkChatProject(createArgs(args, preview));
      } catch (error) {
        setOptimisticFirstTurnText(null);
        onSubmissionErrorRef.current(tab.id);
        throw error;
      }
      if (!handle) {
        setOptimisticFirstTurnText(null);
        return 'refused';
      }

      let bound = false;
      try {
        flushSync(() => {
          bound = onBindProjectRef.current(tab.id, handle.created.project_id);
        });
        return bound ? 'bound' : 'refused';
      } finally {
        handle.releaseGuard();
      }
    },
    [
      createSeeded,
      createArgs,
      createWorkChatProject,
      dropLaunches,
      holdLaunches,
      onBindProjectRef,
      onSubmissionErrorRef,
      onSubmissionStartRef,
      onTaskLaunchesTakenRef,
      existingProjectForLaunches,
      withLaunchPreamble,
      tab,
    ]
  );

  const composerSessionAppearance = useMemo(
    () => ({
      kind: 'default' as const,
      idlePlaceholder: intl.formatMessage(messages.composerPlaceholder, {
        name: object.name,
      }),
    }),
    [intl, object.name]
  );
  const sharedChatProps = {
    projectTitle: tab.title,
    composerPrefill: tab.prefilledPrompt,
    provisionalFirstTurnText: optimisticFirstTurnText,
    provisionalIsSubmitting: isSubmitting,
    analyticsSurface: tab.projectId ? undefined : ('work_home' as const),
    desktopTargetRequirement: 'computer-use-only' as const,
    composerSessionAppearance,
    composerToolbarLeading:
      !tab.projectId && showRunOptions ? (
        <ProjectRunOptionsSelector
          value={runFlags}
          onChange={setRunFlags}
          disabled={isSubmitting}
        />
      ) : null,
    composerAutoFocus: isActive,
    ...lifecycleHandlers,
    composerDraftStorageKey: objectWorkspaceComposerDraftStorageKey(object, tab.id),
    artifactSink,
    workspace,
  };
  const chatProps: ProjectChatProps = tab.projectId
    ? { ...sharedChatProps, projectId: tab.projectId }
    : {
        ...sharedChatProps,
        projectId: undefined,
        onProvisionalSubmit: handleProvisionalSubmit,
      };

  return <ProjectChat {...chatProps} />;
}
